import axios, { AxiosInstance } from 'axios';
import { getEnvVar } from '../config/env';

export type ExecutionOpsResponse<T> = { success: boolean; data: T; error?: string };

class ExecutionOperationsService {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: `${getEnvVar('VITE_FASTAPI_BASE_URL')}/api/v1/admin/execution-operations`,
      timeout: 30000,
      headers: { 'Content-Type': 'application/json' },
    });
    this.api.interceptors.request.use((config) => {
      const token =
        localStorage.getItem('admin_auth_token') ||
        document.cookie.split('; ').find((r) => r.startsWith('auth_token='))?.split('=')[1];
      if (token) config.headers.Authorization = `Bearer ${token}`;
      return config;
    });
  }

  async getStatus() {
    const { data } = await this.api.get<ExecutionOpsResponse<Record<string, unknown>>>('/status');
    return data;
  }

  async getDashboard() {
    const { data } = await this.api.get<ExecutionOpsResponse<Record<string, unknown>>>('/dashboard');
    return data;
  }

  async getWorkerPools() {
    const { data } = await this.api.get<ExecutionOpsResponse<unknown[]>>('/worker-pools');
    return data;
  }

  async getWorkers() {
    const { data } = await this.api.get<ExecutionOpsResponse<unknown[]>>('/workers');
    return data;
  }

  async getWorkersHealth() {
    const { data } = await this.api.get<ExecutionOpsResponse<Record<string, unknown>>>('/workers/health');
    return data;
  }

  async getQueues() {
    const { data } = await this.api.get<ExecutionOpsResponse<unknown[]>>('/queues');
    return data;
  }

  async inspectQueue(physicalQueue: string) {
    const { data } = await this.api.get<ExecutionOpsResponse<Record<string, unknown>>>(
      `/queues/${encodeURIComponent(physicalQueue)}/inspect`
    );
    return data;
  }

  async getQueueConfigurations() {
    const { data } = await this.api.get<ExecutionOpsResponse<unknown[]>>('/queue-configurations');
    return data;
  }

  async getSlos() {
    const { data } = await this.api.get<ExecutionOpsResponse<unknown[]>>('/slos');
    return data;
  }

  async getRetryPolicies() {
    const { data } = await this.api.get<ExecutionOpsResponse<unknown[]>>('/retry-policies');
    return data;
  }

  async getResourceBudgets(subsystemId: string) {
    const { data } = await this.api.get<ExecutionOpsResponse<unknown[]>>('/resource-budgets', {
      params: { subsystem_id: subsystemId },
    });
    return data;
  }

  async getFeatureFlags() {
    const { data } = await this.api.get<ExecutionOpsResponse<unknown[]>>('/feature-flags');
    return data;
  }

  async getMetrics() {
    const { data } = await this.api.get<ExecutionOpsResponse<Record<string, unknown>>>('/metrics');
    return data;
  }

  async getExecutionHistory(limit = 100) {
    const { data } = await this.api.get<ExecutionOpsResponse<unknown[]>>('/execution-history', {
      params: { limit },
    });
    return data;
  }

  async getScheduler() {
    const { data } = await this.api.get<ExecutionOpsResponse<unknown[]>>('/scheduler');
    return data;
  }

  async getDeadLetters() {
    const { data } = await this.api.get<ExecutionOpsResponse<unknown[]>>('/dead-letters');
    return data;
  }

  async getAlerts() {
    const { data } = await this.api.get<ExecutionOpsResponse<unknown[]>>('/alerts');
    return data;
  }

  async getConfigurationAudit(limit = 50) {
    const { data } = await this.api.get<ExecutionOpsResponse<unknown[]>>('/configuration-audit', {
      params: { limit },
    });
    return data;
  }

  async getMigrationStatus() {
    const { data } = await this.api.get<ExecutionOpsResponse<Record<string, unknown>>>('/migration/status');
    return data;
  }

  async getMigrationParity() {
    const { data } = await this.api.get<ExecutionOpsResponse<Record<string, unknown>>>('/migration/parity');
    return data;
  }

  async pauseQueue(enterpriseQueueId: string) {
    const { data } = await this.api.post<ExecutionOpsResponse<Record<string, unknown>>>(
      `/queues/${encodeURIComponent(enterpriseQueueId)}/pause`,
      {}
    );
    return data;
  }

  async resumeQueue(enterpriseQueueId: string) {
    const { data } = await this.api.post<ExecutionOpsResponse<Record<string, unknown>>>(
      `/queues/${encodeURIComponent(enterpriseQueueId)}/resume`,
      {}
    );
    return data;
  }
}

export const executionOperationsService = new ExecutionOperationsService();
